
import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Paper,
  Divider,
} from '@mui/material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { Euro, TrendingUp, AccountBalance, Warning } from '@mui/icons-material';

interface PerformanceFinancialsProps {
  ticketConfig: {
    ticketTypes: Array<{
      name: string;
      quantity: number;
      price: number;
      vatRate: number;
      vatExempt: boolean;
      revenueImpact: boolean;
    }>;
  };
  performance: {
    contingencyRate: number;
    costs: {
      bandFee: number;
      travel: {
        flights: number;
        groundTransport: number;
        other: number;
        total: number;
      };
      accommodation: {
        total: number;
      };
      perDiem: {
        total: number;
      };
      equipment: {
        total: number;
      };
      technical: {
        total: number;
      };
      additional?: {
        marketing?: number;
        insurance?: number;
        visas?: number;
        total: number;
      };
      summary: {
        totalCosts: number;
        contingency: number;
        grandTotal: number;
      };
    };
    revenue?: {
      ticketPrice: number;
      expectedOccupancy: number;
      sponsorship: number;
      merchandiseSales: number;
      otherRevenue: number;
      totalRevenue: number;
      expectedCapacity?: number;
    };
    venue?: {
      capacity: number;
    };
  };
}

const PerformanceFinancials: React.FC<PerformanceFinancialsProps> = ({
  ticketConfig,
  performance
}) => {
  // Calculate ticket revenue
  const calculateTicketRevenue = () => {
    if (!ticketConfig?.ticketTypes) return 0;
    return ticketConfig.ticketTypes.reduce((total, ticket) => {
      if (ticket.revenueImpact) {
        const grossRevenue = ticket.quantity * ticket.price;
        const vatAmount = ticket.vatExempt ? 0 : (grossRevenue * ticket.vatRate);
        const netRevenue = grossRevenue - vatAmount;
        return total + netRevenue;
      }
      return total;
    }, 0);
  };

  const totalRevenue = calculateTicketRevenue();
  const totalCosts = performance.costs.summary.grandTotal;
  const subventionRequired = totalCosts > totalRevenue ? totalCosts - totalRevenue : 0;
  const totalTickets = ticketConfig?.ticketTypes?.reduce((sum, ticket) => sum + ticket.quantity, 0) || 0;
  const venueCapacity = performance.venue?.capacity || 100;
  const occupancyRate = venueCapacity > 0 ? (totalTickets / venueCapacity) * 100 : 0;

  // Calculate break-even
  const avgTicketPrice = totalTickets > 0 ? totalRevenue / totalTickets : 0;
  const breakEvenTickets = avgTicketPrice > 0 ? Math.ceil(totalCosts / avgTicketPrice) : 0;
  const breakEvenPercentage = venueCapacity > 0 ? (breakEvenTickets / venueCapacity) * 100 : 0;

  // Revenue scenarios
  const revenueScenarios = [
    {
      scenario: '50% Full',
      attendance: Math.floor(venueCapacity * 0.5),
      revenue: Math.floor(venueCapacity * 0.5) * avgTicketPrice,
      profit: (Math.floor(venueCapacity * 0.5) * avgTicketPrice) - totalCosts
    },
    {
      scenario: '75% Full',
      attendance: Math.floor(venueCapacity * 0.75),
      revenue: Math.floor(venueCapacity * 0.75) * avgTicketPrice,
      profit: (Math.floor(venueCapacity * 0.75) * avgTicketPrice) - totalCosts
    },
    {
      scenario: 'Sold Out',
      attendance: venueCapacity,
      revenue: venueCapacity * avgTicketPrice,
      profit: (venueCapacity * avgTicketPrice) - totalCosts
    }
  ];

  // Cost breakdown for pie chart (percentages)
  const costBreakdown = [
    { name: 'Band Fee', value: performance.costs.bandFee, color: '#8884d8' },
    { name: 'Travel', value: performance.costs.travel.total, color: '#82ca9d' },
    { name: 'Accommodation', value: performance.costs.accommodation.total, color: '#ffc658' },
    { name: 'Technical', value: performance.costs.technical.total, color: '#ff7300' },
    { name: 'Equipment', value: performance.costs.equipment.total, color: '#00ff88' },
    { name: 'Other', value: (performance.costs.additional?.total || 0), color: '#8dd1e1' },
    { name: 'Contingency', value: performance.costs.summary.contingency, color: '#d084d0' }
  ].filter(item => item.value > 0);

  // Custom tooltip for dark theme
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            p: 1.5,
            borderRadius: 1,
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color }}>
              {entry.name}: €{entry.value?.toFixed(2)}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  // Custom pie tooltip with percentages
  const PieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage = ((data.value / totalCosts) * 100).toFixed(1);
      return (
        <Box
          sx={{
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            p: 1.5,
            borderRadius: 1,
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {data.name}
          </Typography>
          <Typography variant="body2">
            €{data.value.toFixed(2)} ({percentage}%)
          </Typography>
          <Typography variant="caption">
            €{(data.value / totalTickets).toFixed(2)} per ticket
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const formatCurrency = (amount: number) => `€${amount.toFixed(2)}`;

  // Risk assessment
  const getRiskLevel = () => {
    if (breakEvenPercentage > 80) return { level: 'High', color: 'error.main' };
    if (breakEvenPercentage > 60) return { level: 'Medium', color: 'warning.main' };
    return { level: 'Low', color: 'success.main' };
  };

  const risk = getRiskLevel();

  if (!ticketConfig?.ticketTypes || !performance?.costs) {
    return (
      <Card>
        <CardContent>
          <Typography>Financial data not available</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Euro />
          Financial Overview
        </Typography>

        {/* Key Metrics Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.dark', color: 'white' }}>
              <Typography variant="h6">{formatCurrency(totalRevenue)}</Typography>
              <Typography variant="caption">Current Revenue</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.dark', color: 'white' }}>
              <Typography variant="h6">{formatCurrency(totalCosts)}</Typography>
              <Typography variant="caption">Total Costs</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: subventionRequired > 0 ? 'warning.dark' : 'success.dark', color: 'white' }}>
              <Typography variant="h6">{formatCurrency(subventionRequired > 0 ? subventionRequired : totalRevenue - totalCosts)}</Typography>
              <Typography variant="caption">{subventionRequired > 0 ? 'Subvention Required' : 'Profit'}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: risk.color, color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <Warning fontSize="small" />
                <Typography variant="h6">{risk.level}</Typography>
              </Box>
              <Typography variant="caption">Risk Level</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Charts Row */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Cost Breakdown Pie Chart */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 300 }}>
              <Typography variant="h6" gutterBottom>Cost Breakdown</Typography>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={costBreakdown}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={false}
                  >
                    {costBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                  <Legend 
                    wrapperStyle={{ fontSize: '12px' }}
                    iconSize={10}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Revenue Scenarios */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 300 }}>
              <Typography variant="h6" gutterBottom>Revenue Scenarios</Typography>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueScenarios} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis 
                    dataKey="scenario" 
                    tick={{ fill: '#fff', fontSize: 12 }}
                    axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                  />
                  <YAxis 
                    tick={{ fill: '#fff', fontSize: 12 }}
                    axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="revenue" fill="#4CAF50" name="Revenue" />
                  <Bar dataKey="profit" fill="#2196F3" name="Profit/Loss" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* Key Insights */}
        <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp />
            Key Insights
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" gutterBottom>
                <strong>Occupancy:</strong> {occupancyRate.toFixed(1)}% ({totalTickets}/{venueCapacity})
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Avg. Ticket Price:</strong> {formatCurrency(avgTicketPrice)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" gutterBottom>
                <strong>Break-even:</strong> {breakEvenPercentage.toFixed(1)}% capacity
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Cost per Ticket:</strong> {formatCurrency(totalCosts / totalTickets)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography 
                variant="body2" 
                gutterBottom
                sx={{ color: totalRevenue >= totalCosts ? 'success.main' : 'error.main' }}
              >
                <strong>Current P&L:</strong> {formatCurrency(totalRevenue - totalCosts)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Subvention Needed:</strong> {formatCurrency(subventionRequired)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Margin:</strong> {totalRevenue > 0 ? (((totalRevenue - totalCosts) / totalRevenue) * 100).toFixed(1) : 0}%
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </CardContent>
    </Card>
  );
};

export default PerformanceFinancials;